import { useState } from "react";
import { Lock, X, Download, Loader2 } from "lucide-react";

/**
 * Password-protected modal used before sensitive exports (GDPR Art. 20).
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSubmit: (password) => Promise<void>          — direct download
 * - onSubmitEmail: (password) => Promise<void>     — request email with signed link (optional)
 * - busy: boolean — disables the form while operation is running
 */
export default function PasswordPromptModal({ open, onClose, onSubmit, onSubmitEmail, busy }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState("direct"); // 'direct' | 'email'

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    if (mode === "email" && onSubmitEmail) {
      await onSubmitEmail(pwd);
    } else {
      await onSubmit(pwd);
    }
    setPwd("");
  };

  const handleClose = () => {
    setPwd("");
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="password-prompt-modal"
      onClick={busy ? undefined : handleClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="glass-card rounded-md max-w-md w-full p-6 sm:p-8 border-2 border-gold/40 relative"
      >
        <button
          type="button"
          onClick={handleClose}
          disabled={busy}
          aria-label="Cerrar"
          className="absolute top-3 right-3 text-faded hover:text-gold-light p-1 disabled:opacity-30"
          data-testid="password-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>
        <Lock className="w-7 h-7 text-gold mb-4" strokeWidth={1.3} />
        <h3 className="font-display text-2xl text-gold-light mb-2">Confirma tu contraseña</h3>
        <p className="text-sm text-muted leading-relaxed mb-5">
          Vas a descargar todo tu legado en un archivo <code className="text-gold/90">.json</code> descifrado
          (bóveda, mensajes, herederos, activos). Por seguridad, verifica tu contraseña actual.
        </p>

        {onSubmitEmail && (
          <div
            className="flex gap-2 mb-4 text-xs font-label"
            data-testid="export-mode-toggle"
            role="tablist"
          >
            <button
              type="button"
              onClick={() => setMode("direct")}
              className={`px-3 py-1.5 rounded transition ${mode === "direct" ? "bg-gold/15 text-gold border border-gold/40" : "text-faded hover:text-muted border border-transparent"}`}
              data-testid="export-mode-direct"
            >
              Descarga directa
            </button>
            <button
              type="button"
              onClick={() => setMode("email")}
              className={`px-3 py-1.5 rounded transition ${mode === "email" ? "bg-gold/15 text-gold border border-gold/40" : "text-faded hover:text-muted border border-transparent"}`}
              data-testid="export-mode-email"
            >
              Recibir por email
            </button>
          </div>
        )}

        {mode === "email" && (
          <p className="text-[11px] text-gold-light/80 mb-4">
            Recomendado en móvil: te enviaremos un email con un enlace de descarga único, válido 60 min.
          </p>
        )}
        <div className="relative mb-3">
          <input
            type={show ? "text" : "password"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Contraseña actual"
            className="input-field pr-16"
            autoFocus
            autoComplete="current-password"
            data-testid="password-prompt-input"
            disabled={busy}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-faded hover:text-gold-light px-2 py-1"
            tabIndex={-1}
          >
            {show ? "Ocultar" : "Ver"}
          </button>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 justify-end mt-5">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="btn-ghost text-sm"
            data-testid="password-prompt-cancel"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || !pwd}
            className="btn-gold text-sm"
            data-testid="password-prompt-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy
              ? "Preparando…"
              : mode === "email"
                ? "Enviar enlace"
                : "Descargar"}
          </button>
        </div>
        <p className="text-[10px] text-faded mt-4 text-center">
          Límite: 1 descarga cada 24 h. Te enviaremos un email de seguridad.
        </p>
      </form>
    </div>
  );
}
